import type { Tracker, TrackerPunt } from '@fleet/shared';
import { trackerWaarde } from '@fleet/shared';
import { Eyebrow } from './Eyebrow';
import { InfoTip } from './InfoTip';
import { Panel } from './Panel';

const OMHOOG = '#B0543B';
const OMLAAG = '#2A7050';
const GRIJS = '#888888';

/**
 * Six months of shape behind the number.
 *
 * A single percentage cannot tell a step from a wobble, and on this data that
 * distinction matters: road tax stepped once and stayed, while fuel swings every
 * month. Drawn as a plain polyline with no axis — it is there to be glanced at,
 * not read off.
 */
function Sparkline({ punten, kleur }: { punten: (number | null)[]; kleur: string }) {
  const gevuld = punten.filter((v): v is number => v != null);
  if (gevuld.length < 2) return null;
  const min = Math.min(...gevuld);
  const max = Math.max(...gevuld);
  const spanne = max - min || 1;
  const b = 70;
  const h = 20;
  const stap = b / (punten.length - 1);
  // Een maand zonder transacties houdt zijn plek op de as maar krijgt geen punt:
  // de lijn springt eroverheen in plaats van naar nul te duiken.
  const xy = punten
    .map((v, i) => (v == null ? null : ([i * stap, h - ((v - min) / spanne) * h] as const)))
    .filter((p): p is readonly [number, number] => p != null);
  const d = xy.map(([x, y]) => `${x.toFixed(1)},${y.toFixed(1)}`).join(' ');
  const laatste = xy[xy.length - 1]!;
  return (
    <svg
      width={b}
      height={h + 4}
      viewBox={`0 -2 ${b} ${h + 4}`}
      aria-hidden
      style={{ overflow: 'visible' }}
    >
      <polyline
        points={d}
        fill="none"
        stroke={kleur}
        strokeWidth={1.5}
        strokeLinejoin="round"
        opacity={0.55}
      />
      <circle cx={laatste[0]} cy={laatste[1]} r={2.4} fill={kleur} />
    </svg>
  );
}

/** Het verschil met een teken ervoor, in de eenheid van de tegel. */
function verschilLabel(verschil: number, eenheid: Tracker['eenheid']): string {
  const tekst = trackerWaarde(Math.abs(verschil), eenheid);
  return `${verschil > 0 ? '+' : verschil < 0 ? '−' : ''} ${tekst}`.trim();
}

/** Value of one point, total or divided by the cars behind it; null when empty. */
function waardeVan(p: TrackerPunt, perAuto: boolean): number | null {
  if (p.leeg) return null;
  if (!perAuto) return p.waarde;
  return p.autos > 0 ? p.waarde / p.autos : 0;
}

export function TrackerTegel({ tracker, perAuto }: { tracker: Tracker; perAuto: boolean }) {
  const reeks = tracker.reeks;
  const huidig = reeks[reeks.length - 1];
  if (!huidig) return null;

  const gedeeld = perAuto && tracker.perAuto;
  const nu = waardeVan(huidig, gedeeld);
  // De laatste maand mét cijfer, niet botweg de vorige: bij een maand zonder
  // wasbeurten valt er anders niets te vergelijken terwijl de maand ervóór het
  // prima doet.
  const vorige = [...reeks.slice(0, -1)].reverse().find((p) => !p.leeg) ?? null;
  const toen = vorige ? waardeVan(vorige, gedeeld) : null;
  const deel = nu != null && toen != null && toen > 0 ? nu / toen - 1 : null;

  // Stijgende kosten zijn slecht, dalende goed; bij de restwaarde zegt de richting
  // niets, want afschrijven hoort te gebeuren.
  const kleur =
    tracker.kleur === 'neutraal' || deel == null || Math.abs(deel) < 0.005
      ? GRIJS
      : deel > 0
        ? OMHOOG
        : OMLAAG;

  const pct = deel == null ? null : Math.round(deel * 1000) / 10;

  const toelichting = `${gedeeld ? `Per auto, over ${huidig.autos} auto's die deze maand kosten maakten. ` : ''}${tracker.sub}`;

  return (
    <Panel padding={20} style={{ position: 'relative' }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 8,
        }}
      >
        <Eyebrow>{tracker.label}</Eyebrow>
        <InfoTip tekst={toelichting} />
      </div>
      <div style={{ width: 28, height: 1, background: '#111111', margin: '8px 0 10px' }} />
      <div
        style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 8 }}
      >
        <span
          style={{
            fontSize: 'clamp(19px, 1.9vw, 26px)',
            fontWeight: 900,
            color: '#111111',
            letterSpacing: '-0.01em',
            whiteSpace: 'nowrap',
          }}
        >
          {nu == null ? '—' : trackerWaarde(nu, tracker.eenheid)}
        </span>
        <Sparkline
          punten={reeks.map((p) => waardeVan(p, gedeeld))}
          kleur={kleur === GRIJS ? '#2A6085' : kleur}
        />
      </div>

      <div style={{ fontSize: 12, fontWeight: 700, color: kleur, marginTop: 6 }}>
        {pct == null || !vorige || nu == null ? (
          <span style={{ color: GRIJS, fontWeight: 400 }}>
            geen eerdere maand om mee te vergelijken
          </span>
        ) : (
          <>
            {pct > 0 ? '▲' : pct < 0 ? '▼' : '='} {pct > 0 ? '+' : ''}
            {pct.toLocaleString('nl-NL', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%
            t.o.v. {vorige.label.split(' ')[0]}
            <span style={{ color: GRIJS, fontWeight: 400 }}>
              {' '}
              ({verschilLabel(nu - (toen ?? 0), tracker.eenheid)})
            </span>
          </>
        )}
      </div>
    </Panel>
  );
}
